import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, PageHeader, Badge, Btn } from "@/components/UI";
import { ArrowLeft, Package, FlaskConical, Factory, ShieldCheck, Truck, Boxes } from "lucide-react";

/**
 * Auth-gated production tracking page opened via lot-etiket QR code.
 * Path: /track/:lotId
 * If user is not logged in, AuthProvider's Protected redirects to /login.
 */
export default function TrackingPage() {
  const { user } = useAuth();
  const { lotId } = useParams();
  const nav = useNavigate();
  const [trace, setTrace] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get(`/traceability/finished-lot/${lotId}`).catch(() => null),
    ]).then(async ([tr]) => {
      if (!tr) { setErr("Lot bulunamadı"); return; }
      const fl = tr.finished_lot || {};
      const po = tr.production_order || {};
      const [product, customer, brand, so, formula] = await Promise.all([
        fl.product_id ? api.get(`/products/${fl.product_id}`).catch(() => ({})) : Promise.resolve({}),
        fl.customer_id ? api.get(`/customers/${fl.customer_id}`).catch(() => ({})) : Promise.resolve({}),
        fl.brand_id ? api.get(`/brands`).then(bs => bs.find(b => b.id === fl.brand_id) || {}).catch(() => ({})) : Promise.resolve({}),
        po.sales_order_id ? api.get(`/sales-orders/${po.sales_order_id}`).catch(() => ({})) : Promise.resolve({}),
        po.formula_id ? api.get(`/formulas/${po.formula_id}`).catch(() => ({})) : Promise.resolve({}),
      ]);
      const raws = (tr.raw_lots || []).map(r => ({
        lot_code: r.lot?.lot_code,
        material_name: r.material?.name,
        qty_consumed: r.qty_used,
        unit: r.material?.unit,
        expiry_date: r.lot?.expiry_date,
        supplier_name: r.lot?.supplier_name,
      }));
      setTrace({
        finished_lot: fl, production_order: po, sales_order: so,
        product, customer, brand, formula,
        raw_lots: raws,
        shipments: (tr.shipments || []).map(s => ({
          shipment_no: s.shipment_no, carrier: s.carrier, status: s.status,
          shipped_at: s.shipped_at, created_at: s.created_at,
          qty: (s.lines || []).filter(l => l.finished_lot_id === lotId).reduce((a, b) => a + (b.qty || 0), 0),
        })),
      });
    }).catch(e => setErr(e.message || "Yükleme hatası"));
  }, [lotId, user]);

  if (!user) {
    // Not authenticated → redirect to login and come back after
    localStorage.setItem("post_login_redirect", `/track/${lotId}`);
    nav("/login");
    return null;
  }
  if (err) return <div className="p-6"><Card className="p-6 text-center"><div className="text-lg font-semibold text-red-600 mb-2">Kayıt bulunamadı</div><div className="text-sm text-slate-500">{err}</div><Btn className="mt-4" onClick={() => nav("/")}>Kontrol Paneline Dön</Btn></Card></div>;
  if (!trace) return <div className="p-6 text-slate-500">Yükleniyor...</div>;

  const fl = trace.finished_lot || {};
  const po = trace.production_order || {};
  const so = trace.sales_order || {};
  const product = trace.product || {};
  const customer = trace.customer || {};
  const brand = trace.brand || {};
  const formula = trace.formula || {};
  const raws = trace.raw_lots || [];
  const shipments = trace.shipments || [];

  return (
    <div>
      <PageHeader
        title={`Lot ${fl.lot_code || ""}`}
        description="Bu etiket üzerindeki karekod okutularak açıldı — üretim izlenebilirlik dosyası"
        actions={<Btn variant="secondary" onClick={() => nav(-1)}><ArrowLeft size={14}/> Geri</Btn>}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 bg-gradient-to-br from-fuchsia-50 to-pink-50 dark:from-fuchsia-900/20 dark:to-pink-900/20 border-fuchsia-200/60 dark:border-fuchsia-500/30">
          <div className="text-[10px] uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 mb-1">Müşteri</div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{customer.legal_name || "—"}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border-violet-200/60 dark:border-violet-500/30">
          <div className="text-[10px] uppercase tracking-wider text-violet-700 dark:text-violet-300 mb-1">Marka</div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{brand.name || "—"}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200/60 dark:border-emerald-500/30">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1">Ürün</div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{product.name || "—"}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{product.fill_qty}{product.fill_unit}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-sky-50 to-cyan-50 dark:from-sky-900/20 dark:to-cyan-900/20 border-sky-200/60 dark:border-sky-500/30">
          <div className="text-[10px] uppercase tracking-wider text-sky-700 dark:text-sky-300 mb-1">Üretim / SKT</div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100 font-mono">{(fl.mfg_date || "").slice(0, 10) || "—"}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">SKT: {(fl.expiry_date || "").slice(0, 10) || "—"}</div>
        </Card>
      </div>

      <Card className="p-4 mb-4">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Factory size={14} className="text-indigo-600 dark:text-indigo-400"/> Üretim Bilgileri
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Üretim Emri</div><div className="font-mono">{po.prod_no || "—"}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Planlanan Adet</div><div className="font-mono">{po.planned_units || 0}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Üretilen Adet</div><div className="font-mono">{fl.qty_produced || 0}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Durum</div><Badge level={po.status === "completed" ? "ok" : "attention"}>{po.status || "—"}</Badge></div>
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Satış Siparişi</div><div className="font-mono">{so.order_no || "—"}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Formül</div><div className="font-mono">{formula.code || "—"} {formula.version || ""}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Batch (kg)</div><div className="font-mono">{po.actual_bulk_kg || po.planned_batch_kg || 0}</div></div>
          <div><div className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Stok (Adet)</div><div className="font-mono">{fl.qty_available || 0}</div></div>
        </div>
      </Card>

      <Card className="p-4 mb-4">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Boxes size={14} className="text-teal-600 dark:text-teal-400"/> Kullanılan Ham Madde Lotları ({raws.length})
        </div>
        {raws.length === 0 ? <div className="text-xs text-slate-400">Kayıt yok</div> :
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 dark:border-slate-700/50">
              <tr className="text-[10px] uppercase text-slate-500 dark:text-slate-400">
                <th className="text-left py-1.5">Lot</th><th className="text-left">Malzeme</th>
                <th className="text-right">Miktar</th><th className="text-left">SKT</th><th className="text-left">Tedarikçi</th>
              </tr>
            </thead>
            <tbody>
              {raws.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 dark:border-slate-700/40">
                  <td className="py-1.5 font-mono">{r.lot_code}</td>
                  <td>{r.material_name}</td>
                  <td className="text-right font-mono">{(r.qty_consumed || 0).toFixed(2)} {r.unit}</td>
                  <td className="font-mono">{(r.expiry_date || "").slice(0, 10) || "—"}</td>
                  <td>{r.supplier_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>}
      </Card>

      {shipments.length > 0 && (
        <Card className="p-4 mb-4">
          <div className="text-sm font-semibold mb-3 flex items-center gap-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <Truck size={14} className="text-emerald-600 dark:text-emerald-400"/> Sevkiyatlar ({shipments.length})
          </div>
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 dark:border-slate-700/50">
              <tr className="text-[10px] uppercase text-slate-500 dark:text-slate-400">
                <th className="text-left py-1.5">Sevkiyat No</th><th className="text-left">Taşıyıcı</th>
                <th className="text-right">Adet</th><th className="text-left">Durum</th><th className="text-left">Tarih</th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((s, i) => (
                <tr key={i} className="border-b border-slate-100 dark:border-slate-700/40">
                  <td className="py-1.5 font-mono">{s.shipment_no}</td>
                  <td>{s.carrier || "—"}</td>
                  <td className="text-right font-mono">{s.qty}</td>
                  <td><Badge level={s.status === "delivered" ? "ok" : "attention"}>{s.status}</Badge></td>
                  <td className="font-mono">{(s.shipped_at || s.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div className="text-xs text-slate-400 dark:text-slate-500 text-center py-2">
        Bu sayfa yetkili kullanıcı girişi gerektirir. Meta Pharma ERP · İzlenebilirlik
      </div>
    </div>
  );
}
